import { CSSProperties, ReactNode, forwardRef } from "react"
import NextLink from "next/link"
import { observer } from "mobx-react"
import { useStore } from "@features/common"
import { config } from "@lib/config"
import { DEFAULT_LANG } from "@providers"

import type { LanguageISO } from "@providers"

export interface RouterLinkProps extends RootProps {
  uri: string
  routing?: boolean
  availableLangs?: LanguageISO[]
  forceLang?: string
}

interface RootProps {
  className?: string
  style?: CSSProperties
  onClick?: () => void
  children: ReactNode
  target?: string
  lang?: string
}

export const RouterLink = observer(function RouterLink(props: RouterLinkProps) {
  const {
    className,
    style,
    uri,
    routing = true,
    onClick,
    children,
    target,
    availableLangs,
    forceLang,
    ...otherProps
  } = props
  const { GlobalModel } = useStore()

  const handleClick = () => {
    if (onClick) onClick()
  }

  // eslint-disable-next-line
  const langUriPrefix = GlobalModel?.lang
    ? `/${
        GlobalModel.accessLanguages.includes(GlobalModel.lang) ||
        (availableLangs ?? []).includes(GlobalModel.lang)
          ? GlobalModel.lang
          : DEFAULT_LANG
      }`
    : ""
  const resolvedForceLang = forceLang && "/" + forceLang
  const resolveHref = routing
    ? `${resolvedForceLang || langUriPrefix}${uri}`
    : uri
  const href = `${routing ? config.data.prefix : ""}${resolveHref}`

  return (
    <NextLink href={resolveHref}>
      <LinkRoot
        href={href}
        onClick={handleClick}
        className={className}
        style={style}
        target={target}
        {...otherProps}
      >
        {children}
      </LinkRoot>
    </NextLink>
  )
})

const LinkRoot = forwardRef((props: RootProps & { href?: string }, ref) => {
  const resolve: RootProps = { ...props }
  // @ts-ignore
  delete resolve.uri
  // @ts-ignore
  resolve.ref = ref

  return <a {...resolve}>{props.children}</a>
})
